import classNames from "classnames";
import { AsYouType, CountryCode, getExampleNumber } from "libphonenumber-js";
import examples from "libphonenumber-js/examples.mobile.json";
import { FocusEventHandler, useState } from "react";
import { COUNTRIES, PRIORITY_COUNTRY_CODES } from "../../utils/constants";
import { parsePhoneNumber } from "../../utils/helpers";
import Dropdown from "../dropdown/dropdown";
import { CaretDown } from "../icon/downcaret";
import { Icon } from "../icon/icon";
import { TextInput } from "./textInput";

export const PhoneInputSet = ({
  phoneNumber,
  className,
  value,
  onChange,
  onBlur,
  errors,
  name,
  disabled,
  placeholder = "Enter Phone Number",
  title = "Phone Number",
}: {
  phoneNumber?: string;
  className?: string;
  value: string;
  onChange: (val: string) => void;
  onBlur: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  errors?: string;
  name: string;
  disabled?: boolean;
  placeholder?: string;
  title?: string;
}) => {
  const [search, setSearch] = useState<string>("");
  const [country, setCountry] = useState<CountryCode>(
    phoneNumber ? (parsePhoneNumber(phoneNumber)?.country ?? "NG") : "NG",
  );

  return (
    <div className={classNames("flex items-start gap-[6px]", className)}>
      <Dropdown
        position="bottom start"
        customMenuItems={({ close }) => {
          return (
            <div className="relative flex max-h-[312px] max-w-[234px] flex-col overflow-y-scroll rounded-lg border border-grey-100 bg-white text-sm">
              <div className="sticky top-0 flex items-center gap-2 border-b bg-white p-4 text-grey-500">
                <Icon svg="search" width={16} height={16} />
                <input
                  type="text"
                  placeholder="Search"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value ?? "");
                  }}
                  className="flex-1 outline-none autofill:bg-transparent focus:outline-none autofill:focus:bg-transparent"
                />
              </div>
              {Object.keys(COUNTRIES)
                .filter((k) =>
                  search
                    ? COUNTRIES[k].name
                        .toLowerCase()
                        .includes(search?.toLowerCase())
                    : true,
                )
                .sort((a, b) => {
                  const aIsPriority = PRIORITY_COUNTRY_CODES.includes(
                    a as CountryCode,
                  );
                  const bIsPriority = PRIORITY_COUNTRY_CODES.includes(
                    b as CountryCode,
                  );

                  if (aIsPriority && bIsPriority) {
                    return (
                      PRIORITY_COUNTRY_CODES.indexOf(a as CountryCode) -
                      PRIORITY_COUNTRY_CODES.indexOf(b as CountryCode)
                    );
                  }

                  if (aIsPriority) return -1;
                  if (bIsPriority) return 1;

                  return COUNTRIES[a].name.localeCompare(COUNTRIES[b].name);
                })
                .map((k) => {
                  return (
                    <button
                      key={k}
                      type="button"
                      className="w-full border-b p-4 text-left font-bold last:border-b-0"
                      onClick={() => {
                        setSearch("");
                        onChange(COUNTRIES[k].countryCode);
                        setCountry(k as CountryCode);
                        close();
                      }}
                    >
                      {COUNTRIES[k].flag} {COUNTRIES[k].name} (
                      {COUNTRIES[k].countryCode})
                    </button>
                  );
                })}
            </div>
          );
        }}
        className="h-full !max-w-fit items-center justify-center"
        closeOnClick={false}
        disabled={disabled}
      >
        <button
          type="button"
          className="w-fit flex-1 items-stretch gap-0 self-stretch rounded-lg border border-grey-100 bg-grey-50 p-3 text-xl font-bold"
          disabled={disabled}
        >
          <p>
            <span>{COUNTRIES[country].flag}</span>
            <CaretDown
              width={16}
              height={16}
              className="!h-fit text-grey-600"
            />
          </p>
        </button>
      </Dropdown>

      <TextInput
        title={title}
        hasTitle
        name={name}
        type="text"
        placeholder={placeholder}
        value={value}
        disabled={disabled}
        onChange={(e) => {
          const countryDial = COUNTRIES[country].countryCode; // e.g. "+234"

          const example = getExampleNumber(country, examples);

          const nationalMaxLength =
            (example?.nationalNumber.length ?? 0) + countryDial.length;

          const rawDigits = e.target.value
            .replace(/\D/g, "")
            .slice(0, nationalMaxLength); // user typed digits only

          let fullNumber = "";

          if (
            rawDigits === countryDial.replace("+", "") ||
            rawDigits.length < countryDial.replace("+", "").length
          ) {
            fullNumber = countryDial;
          } else if (
            rawDigits.startsWith(countryDial.replace("+", "")) &&
            rawDigits !== countryDial.replace("+", "")
          ) {
            fullNumber = countryDial + rawDigits.slice(countryDial.length - 1);
          } else {
            fullNumber = countryDial + rawDigits;
          }

          const formatter = new AsYouType();
          const formatted = formatter.input(fullNumber);

          onChange(formatted);
        }}
        onBlur={onBlur}
        errors={errors}
        containerClassName="flex-1"
      />
    </div>
  );
};
